import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * Words for a state most projects never enter.
 *
 * Deliberately NOT in the `ide` dictionary. That table's gate requires every
 * string in it to be harvested from a walk of the preview surface, and this
 * copy appears only while a project is pinned to its source viewport — a
 * state the walk cannot construct (measured: the walk does not render the
 * preview canvas at all). Putting it there would have meant either a red gate
 * forever or teaching the walk to build a canvas, and neither is what the
 * gate is for.
 *
 * `Record<LocaleCode, …>` keeps `tsc` as the key-parity gate, the same way
 * `preview-review-outcome-copy.ts` does.
 */
export interface ViewportPolicyCopy {
  /** Why the other breakpoints are off: the project pinned itself. */
  pinnedToSource: string
  /** The action that releases the pin, at project level. */
  makeResponsive: string
}

const EN: ViewportPolicyCopy = {
  pinnedToSource:
    'This project follows the viewport its source was drawn at, so other breakpoint presets are unavailable.',
  makeResponsive: 'Make it responsive',
}

const KO: ViewportPolicyCopy = {
  pinnedToSource: '이 프로젝트는 소스가 그려진 뷰포트를 따르므로 다른 브레이크포인트 프리셋을 사용할 수 없습니다.',
  makeResponsive: '반응형으로 바꾸기',
}

const JA: ViewportPolicyCopy = {
  pinnedToSource:
    'このプロジェクトはソースが描かれたビューポートに従うため、他のブレークポイントプリセットは使用できません。',
  makeResponsive: 'レスポンシブにする',
}

const ZH: ViewportPolicyCopy = {
  pinnedToSource: '此项目遵循其源稿的视口，因此无法使用其他断点预设。',
  makeResponsive: '改为响应式',
}

const ZH_TW: ViewportPolicyCopy = {
  pinnedToSource: '此專案遵循其原稿的視口，因此無法使用其他中斷點預設。',
  makeResponsive: '改為響應式',
}

export const VIEWPORT_POLICY_COPY: Record<LocaleCode, ViewportPolicyCopy> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  'zh-TW': ZH_TW,
}
